import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiImage, FiScissors } from "react-icons/fi";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Button from "../components/common/Button";
import Loader from "../components/common/Loader";
import * as serviceService from "../services/serviceService";

import hennaPic1 from "../assets/images/hennaPic1.jpg";
import hennaPic2 from "../assets/images/hennaPic2.jpg";
import hennaPic3 from "../assets/images/hennaPic3.jpg";
import hennaPic4 from "../assets/images/hennaPic4.jpg";
import hennaPic5 from "../assets/images/hennaPic5.jpg";
import hennaPic6 from "../assets/images/hennaPic6.jpg";
import hennaPic7 from "../assets/images/hennaPic7.jpg";
import hennaPic8 from "../assets/images/hennaPic8.jpg";

const safeCallGetServices = async () => {
  if (typeof serviceService.getAllServices === "function") {
    return serviceService.getAllServices();
  }

  if (typeof serviceService.getServices === "function") {
    return serviceService.getServices();
  }

  if (typeof serviceService.fetchServices === "function") {
    return serviceService.fetchServices();
  }

  throw new Error("Service list method was not found in serviceService.");
};

const staticGalleryItems = [
  {
    id: "static-1",
    imageUrl: hennaPic1,
    title: "Elegant Henna Detail",
    category: "Portfolio",
    description: "A graceful design to inspire your next appointment.",
  },
  {
    id: "static-2",
    imageUrl: hennaPic2,
    title: "Celebration Style",
    category: "Portfolio",
    description: "A polished look for joyful moments and beautiful occasions.",
  },
  {
    id: "static-3",
    imageUrl: hennaPic3,
    title: "Classic Pattern",
    category: "Portfolio",
    description: "Timeless henna artistry with refined detail.",
  },
  {
    id: "static-4",
    imageUrl: hennaPic4,
    title: "Soft Bridal Inspiration",
    category: "Portfolio",
    description: "A beautiful bridal-inspired look with elegant flow.",
  },
  {
    id: "static-5",
    imageUrl: hennaPic5,
    title: "Modern Henna Accent",
    category: "Portfolio",
    description: "A stylish design with a clean and expressive finish.",
  },
  {
    id: "static-6",
    imageUrl: hennaPic6,
    title: "Intricate Hand Design",
    category: "Portfolio",
    description: "Detailed artistry created with care and balance.",
  },
  {
    id: "static-7",
    imageUrl: hennaPic7,
    title: "Statement Design",
    category: "Portfolio",
    description: "A striking look for clients who want something memorable.",
  },
  {
    id: "static-8",
    imageUrl: hennaPic8,
    title: "Elegant Occasion Look",
    category: "Portfolio",
    description: "A lovely example of henna designed for special moments.",
  },
];

const GalleryImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className="hero-overlay flex h-56 items-center justify-center text-white">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/15">
            <FiImage size={30} />
          </div>
          <p className="mt-4 text-sm font-semibold uppercase tracking-[0.2em]">
            Henna Gallery
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-56 w-full overflow-hidden bg-soft-cream">
      <img
        src={src}
        alt={alt}
        className="h-full w-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const GalleryCard = ({ item, onViewService, onBookNow, showActions = true }) => {
  return (
    <article className="content-card overflow-hidden transition-transform duration-200 hover:-translate-y-1">
      <GalleryImage
        src={item?.imageUrl || item?.serviceImageUrl}
        alt={item?.title || item?.serviceName || "Henna design"}
      />

      <div className="p-5">
        <div className="flex items-center gap-2 text-golddeep">
          <FiScissors />
          <span className="text-xs font-semibold uppercase tracking-wide">
            {item?.category || item?.serviceType || "Henna"}
          </span>
        </div>

        <h3 className="mt-3 text-xl font-semibold text-henna">
          {item?.title || item?.serviceName || "Henna Design"}
        </h3>

        <p className="mt-3 text-sm leading-6 text-muted-warm">
          {item?.description || item?.serviceDescription
            ? (item.description || item.serviceDescription).length > 110
              ? `${(item.description || item.serviceDescription).slice(0, 110)}...`
              : item.description || item.serviceDescription
            : "A beautiful design from the Nayra Henna collection."}
        </p>

        {showActions && item?.id && typeof item.id === "number" && (
          <div className="mt-5 flex flex-wrap gap-3">
            <Button variant="secondary" onClick={() => onViewService(item.id)}>
              View Service
            </Button>

            <Button variant="primary" onClick={() => onBookNow(item.id)}>
              Book This Style
            </Button>
          </div>
        )}
      </div>
    </article>
  );
};

const Gallery = () => {
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState("ALL");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);
        setPageError("");

        const data = await safeCallGetServices();

        if (data?.statusCode && data.statusCode !== 200) {
          throw new Error(data.message || "Failed to load gallery services.");
        }

        const serviceList = Array.isArray(data?.serviceList) ? data.serviceList : [];
        const activeServices = serviceList
          .filter((service) => service?.active !== false)
          .map((service) => ({
            ...service,
            imageUrl: service?.serviceImageUrl || "",
          }));

        setServices(activeServices);
      } catch (err) {
        setPageError(err?.message || "Unable to load gallery.");
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, []);

  const categories = useMemo(() => {
    const types = new Set(
      services.map((service) => service?.serviceType).filter(Boolean)
    );
    return ["ALL", ...Array.from(types)];
  }, [services]);

  const visibleBackendItems =
    activeCategory === "ALL"
      ? services
      : services.filter((service) => service?.serviceType === activeCategory);

  const handleViewService = (serviceId) => {
    navigate(`/services?serviceId=${serviceId}`);
  };

  const handleBookNow = (serviceId) => {
    navigate(`/booking?serviceId=${serviceId}`);
  };

  return (
    <div className="page-section">
      <PageContainer>
        <SectionTitle
          eyebrow="Gallery"
          title="A closer look at the artistry"
          subtitle="Explore design inspiration, browse available service looks, and get a feel for the beauty and detail of Nayra Henna."
          className="mb-8"
        />

        <div className="content-card-lg p-6 md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-henna">
                Service gallery
              </h2>
              <p className="mt-2 text-sm text-muted-warm">
                Browse images connected to active services and filter by service
                type to narrow your view.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={activeCategory === category ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {pageError ? (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{pageError}</p>
            </div>
          ) : loading ? (
            <div className="mt-8">
              <Loader text="Loading gallery..." />
            </div>
          ) : (
            <div className="mt-8 responsive-three-col">
              {visibleBackendItems.map((item) => (
                <GalleryCard
                  key={item.id}
                  item={item}
                  onViewService={handleViewService}
                  onBookNow={handleBookNow}
                  showActions
                />
              ))}
            </div>
          )}

          {!loading && !pageError && visibleBackendItems.length === 0 && (
            <div className="empty-state">
              <p className="text-base font-medium text-[#3b2f2f]">
                No gallery items were found for this category.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 content-card-lg p-6 md:p-8">
          <div>
            <h2 className="text-2xl font-semibold text-henna">
              More design inspiration
            </h2>
            <p className="mt-2 text-sm text-muted-warm">
              A curated collection of portfolio images to help you explore
              styles, patterns, and inspiration for your next booking.
            </p>
          </div>

          <div className="mt-8 responsive-three-col">
            {staticGalleryItems.map((item) => (
              <GalleryCard
                key={item.id}
                item={item}
                onViewService={handleViewService}
                onBookNow={handleBookNow}
                showActions={false}
              />
            ))}
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default Gallery;